import { useEffect, useState } from 'react'
import LoginPage from './features/auth/LoginPage'
import DashboardPage from './features/dashboard/DashboardPage'
import LandingPage from './features/landing/LandingPage'
import { clearSession, loadSession, saveSession } from './features/auth/authStorage'
import {
  applyThemeToDocument,
  loadThemePreference,
  resolveEffectiveTheme,
  saveThemePreference,
  watchSystemThemeChanges,
} from './features/theme/themePreference'
import type { AuthMode } from './features/auth/authStorage'
import type { MemberIdentity } from './features/auth/types'
import type { ThemePreference } from './features/theme/themePreference'
import LiquidGlassFilterDefs from './features/shared/LiquidGlassFilterDefs'

const VIEWS = {
  dashboard: 'dashboard',
  home: 'home',
  login: 'login',
} as const

const ROUTES = {
  dashboard: '/dashboard',
  home: '/',
  login: '/login',
  signup: '/signup',
} as const

type View = (typeof VIEWS)[keyof typeof VIEWS]
type AuthScreenMode = 'mock' | 'signin' | 'signup'
type RouteKey = 'dashboard' | 'home' | 'login' | 'signup'

interface AppState {
  authMode: AuthMode
  authScreenMode: AuthScreenMode
  member: MemberIdentity | null
  redirectPath: string | null
  view: View
}

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/'
  }

  return pathname.replace(/\/+$/, '') || '/'
}

function resolveRoute(pathname: string): RouteKey {
  const normalizedPathname = normalizePathname(pathname)

  switch (normalizedPathname) {
    case ROUTES.dashboard:
      return 'dashboard'
    case ROUTES.login:
      return 'login'
    case ROUTES.signup:
      return 'signup'
    default:
      return 'home'
  }
}

function toMemberIdentity(
  session: ReturnType<typeof loadSession>
): MemberIdentity | null {
  if (!session) {
    return null
  }

  return {
    email: session.email,
    name: session.name,
  }
}

function resolveAppState(
  pathname: string,
  session: ReturnType<typeof loadSession>,
  currentMember: MemberIdentity | null = null,
  currentAuthMode: AuthMode = 'mock'
): AppState {
  const route = resolveRoute(pathname)
  const persistedMember = toMemberIdentity(session)
  const effectiveMember = persistedMember ?? currentMember
  const effectiveAuthMode = session?.mode ?? (effectiveMember ? currentAuthMode : 'mock')

  if (route === 'dashboard') {
    if (effectiveMember) {
      return {
        authMode: effectiveAuthMode,
        authScreenMode: 'signin',
        member: effectiveMember,
        redirectPath: null,
        view: VIEWS.dashboard,
      }
    }

    return {
      authMode: 'mock',
      authScreenMode: 'signin',
      member: null,
      redirectPath: ROUTES.login,
      view: VIEWS.login,
    }
  }

  if (route === 'signup') {
    return {
      authMode: effectiveAuthMode,
      authScreenMode: 'signup',
      member: effectiveMember,
      redirectPath: null,
      view: VIEWS.login,
    }
  }

  if (route === 'login') {
    return {
      authMode: effectiveAuthMode,
      authScreenMode: 'signin',
      member: effectiveMember,
      redirectPath: null,
      view: VIEWS.login,
    }
  }

  return {
    authMode: effectiveAuthMode,
    authScreenMode: 'signin',
    member: effectiveMember,
    redirectPath: null,
    view: VIEWS.home,
  }
}

function syncPath(pathname: string, replace = false) {
  const nextPathname = normalizePathname(pathname)
  const currentPathname = normalizePathname(window.location.pathname)

  if (currentPathname === nextPathname) {
    return
  }

  const historyMethod = replace ? 'replaceState' : 'pushState'
  window.history[historyMethod](null, '', nextPathname)
}

function resolveInitialAppState(): AppState {
  return resolveAppState(window.location.pathname, loadSession())
}

export default function App() {
  const [initialState] = useState(resolveInitialAppState)

  const [view, setView] = useState<View>(initialState.view)
  const [authScreenMode, setAuthScreenMode] = useState<AuthScreenMode>(initialState.authScreenMode)
  const [member, setMember] = useState<MemberIdentity | null>(initialState.member)
  const [authMode, setAuthMode] = useState<AuthMode>(initialState.authMode)
  const [themePreference, setThemePreference] = useState<ThemePreference>(loadThemePreference)

  useEffect(() => {
    if (initialState.redirectPath) {
      syncPath(initialState.redirectPath, true)
    }
  }, [initialState])

  useEffect(() => {
    saveThemePreference(themePreference)

    const isLoginView = view === VIEWS.login
    const effectiveTheme = isLoginView ? 'light' : resolveEffectiveTheme(themePreference)
    applyThemeToDocument(effectiveTheme)

    if (themePreference !== 'system' || isLoginView) {
      return
    }

    return watchSystemThemeChanges((nextTheme) => {
      applyThemeToDocument(nextTheme)
    })
  }, [themePreference, view])

  useEffect(() => {
    function handlePopState() {
      const nextState = resolveAppState(window.location.pathname, loadSession(), member, authMode)

      setView(nextState.view)
      setAuthScreenMode(nextState.authScreenMode)
      setMember(nextState.member)
      setAuthMode(nextState.authMode)

      if (nextState.redirectPath) {
        syncPath(nextState.redirectPath, true)
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [authMode, member])

  function openHome(replace = false) {
    setView(VIEWS.home)
    syncPath(ROUTES.home, replace)
  }

  function openLogin(nextMode: AuthScreenMode = 'signin', replace = false) {
    setView(VIEWS.login)
    setAuthScreenMode(nextMode)
    syncPath(nextMode === 'signup' ? ROUTES.signup : ROUTES.login, replace)
  }

  function openDashboard(replace = false) {
    setView(VIEWS.dashboard)
    syncPath(ROUTES.dashboard, replace)
  }

  function handleMockLogin(nextMember: MemberIdentity) {
    setMember(nextMember)
    setAuthMode('mock')
    openDashboard()
  }

  function handleRealLogin(nextMember: MemberIdentity) {
    setMember(nextMember)
    setAuthMode('real')
    saveSession({
      email: nextMember.email,
      name: nextMember.name,
      mode: 'real',
      loggedInAt: new Date().toISOString(),
    })
    openDashboard()
  }

  function handleLogout() {
    clearSession()
    setMember(null)
    setAuthMode('mock')
    openLogin('signin')
  }

  const page =
    view === VIEWS.login ? (
      <LoginPage
        mode={authScreenMode}
        onModeChange={openLogin}
        onMockLogin={handleMockLogin}
        onRealLogin={handleRealLogin}
      />
    ) : view === VIEWS.dashboard && member ? (
      <DashboardPage
        member={member}
        authMode={authMode}
        onBackHome={() => openHome()}
        onLogout={handleLogout}
        themePreference={themePreference}
        onThemePreferenceChange={setThemePreference}
      />
    ) : (
      <LandingPage
        onOpenLogin={() => openLogin('signin')}
        onOpenSignUp={() => openLogin('signup')}
      />
    )

  return (
    <>
      <LiquidGlassFilterDefs />
      {page}
    </>
  )
}
